/**
 * The World view's live lines, fed (RFC 0336 3f, S10).
 *
 * Which sessions get a line comes off every project's registry, which the
 * window already holds (`useWorldSessions`), so choosing costs nothing. What a
 * line SAYS needs the session's own small header, so only the sessions that
 * get a line are opened, only while the view is open, and never more than
 * `LINE_READ_LIMIT` of them.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  projectSessionStripActivity,
  type SessionRunStatus,
  type SessionStripActivity,
} from '@mod/core/model'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDocuments } from '../../services/world/repo/useDocument'
import type { WorldSessionRow } from '../../services/world/useWorldSessions'
import {
  lineCandidates,
  lineText,
  nextFinished,
  RANK,
  stillAsking,
  type WorldLine,
} from './liveLines'

/** At most this many sessions are opened to say what they are doing. */
export const LINE_READ_LIMIT = 10

/**
 * The lines, from candidates and what each session's header says, read for
 * the state the line is in (`undefined`: the session is not read yet).
 *
 * EVERY CANDIDATE KEEPS A LINE. One the registry calls asking whose header
 * has no question (answered, and the claim not yet cleared) is drawn as
 * work waiting on a next move, with what it last said, not dropped.
 */
export function confirmedLines(
  candidates: ReturnType<typeof lineCandidates>,
  activityOf: (
    sessionId: string,
    status: SessionRunStatus,
  ) => SessionStripActivity | null | undefined,
): WorldLine[] {
  const out: WorldLine[] = []
  for (const candidate of candidates) {
    const { row } = candidate
    let state = candidate.state
    let activity = activityOf(row.id, readAs(candidate))
    if (state === 'asking' && !stillAsking(activity)) {
      state = 'waiting'
      activity = activityOf(row.id, 'idle')
    }
    out.push({
      sessionId: row.id,
      title: row.title,
      projectId: row.projectId,
      projectName: row.projectName,
      ...(row.updatedAt != null ? { updatedAt: row.updatedAt } : {}),
      state,
      ...lineText(state, row.status, activity),
    })
  }
  // A fallback changes a line's state, so it changes its place: an answered
  // question goes back among the waiting. Stable, so the order within a state
  // is the candidates' own.
  return out
    .map((line, i) => ({ line, i }))
    .sort((a, b) => RANK[a.line.state] - RANK[b.line.state] || a.i - b.i)
    .map(({ line }) => line)
}

export function useWorldLines(
  rows: readonly WorldSessionRow[],
  opts: {
    worldAgentId: string | null
    /** Read sessions only while the lines are on screen. */
    open: boolean
    /** The selected line, read even when scrolled past the first few, so
     *  walking down the list shows what each one said. */
    alsoRead?: readonly string[]
  },
): {
  lines: WorldLine[]
  /** A finished line has been read: it goes. */
  seen: (sessionIds: readonly string[]) => void
} {
  // FINISHED is something this window saw happen, so it is watched always,
  // not only while the view is open: work that ends while you are elsewhere
  // is exactly what the line is for. The watching is the registry rows,
  // which cost nothing more.
  const before = useRef<Map<string, SessionRunStatus | undefined>>(new Map())
  const [finished, setFinished] = useState<ReadonlySet<string>>(new Set())
  useEffect(() => {
    const was = before.current
    setFinished((prev) => {
      const next = nextFinished(prev, was, rows)
      return sameSet(prev, next) ? prev : next
    })
    before.current = new Map(rows.map((r) => [r.id, r.status]))
  }, [rows])

  const candidates = useMemo(
    () => lineCandidates(rows, { worldAgentId: opts.worldAgentId, finished }),
    [rows, opts.worldAgentId, finished],
  )
  // Content-keyed, so a fresh array of the same ids reads nothing new.
  const alsoKey = (opts.alsoRead ?? []).join(' ')
  const readIds = useMemo(() => {
    if (!opts.open) return []
    const ids = candidates.slice(0, LINE_READ_LIMIT).map((c) => c.row.id)
    const known = new Set(candidates.map((c) => c.row.id))
    for (const id of alsoKey ? alsoKey.split(' ') : [])
      if (known.has(id) && !ids.includes(id)) ids.push(id)
    // SORTED: the subscriptions are keyed by the id list, so a reorder (a
    // line moving from waiting to working) must not rebuild every one.
    return ids.sort() as AnyDocumentId[]
  }, [opts.open, candidates, alsoKey])
  const docs = useDocuments<unknown>(readIds)

  const lines = useMemo(
    () =>
      confirmedLines(candidates, (id, status) => {
        const doc = docs.get(id)
        return doc === undefined
          ? undefined
          : projectSessionStripActivity(doc, status)
      }),
    [candidates, docs],
  )

  const seen = useCallback((sessionIds: readonly string[]) => {
    setFinished((prev) => {
      if (!sessionIds.some((id) => prev.has(id))) return prev
      const next = new Set(prev)
      for (const id of sessionIds) next.delete(id)
      return next
    })
  }, [])

  // DRAWN BOTTOM-UP, like a thread: the most urgent and latest sit next to
  // the box, so ↑ from the box reaches them first and the list opens
  // scrolled to them.
  const drawn = useMemo(() => [...lines].reverse(), [lines])
  return { lines: drawn, seen }
}

/** The header is read for the state the LINE is in. */
function readAs(
  c: ReturnType<typeof lineCandidates>[number],
): SessionRunStatus {
  if (c.state === 'asking') return 'asking'
  if (c.state === 'done' || c.state === 'waiting') return 'idle'
  return c.row.status ?? 'running'
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const x of a) if (!b.has(x)) return false
  return true
}
